<template>
  <div>
    <b-overlay :show="isLoading" rounded="sm">
      <b-container fluid>
        <b-navbar toggleable="lg" type="dark" variant="light">
          <b-navbar-brand href="#" class="text-dark">
            <strong>{{clientData && clientData.company_name}} - Identifiers</strong>
          </b-navbar-brand>

          <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

          <b-collapse id="nav-collapse" is-nav>
            <!-- Right aligned nav items -->
            <b-navbar-nav class="ml-auto">
              <b-nav-form>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="showModal = !showModal"
                  class="mx-2"
                >
                  <font-awesome-icon icon="plus" />
                </b-button>

                <b-button
                  class="mr-2"
                  variant="outline-info"
                  size="sm"
                  @click="getIdentifiersTableData"
                >
                  <font-awesome-icon icon="sync" />
                </b-button>
                <b-form-group>
                  <b-input-group size="sm">
                    <b-form-input
                      v-model="filter"
                      type="search"
                      id="filterInput"
                      placeholder="Type to Search"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-nav-form>
            </b-navbar-nav>
          </b-collapse>
        </b-navbar>

        <b-table
          class="mx-1"
          bordered
          sticky-header="calc(100vh - 200px)"
          ref="table"
          id="my-table"
          fixed
          :busy.sync="isLoading"
          :items="tableItems"
          :fields="tableFields"
          :filter="filter"
          :per-page="perPage"
          :current-page="currentPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          responsive="sm"
        >
          <template v-slot:cell(actions)="data">
            <div class="text-nowrap">
              <b-button
                size="sm"
                class="mr-2"
                type="button"
                variant="outline-info"
                @click="onEditIdentifier(data)"
              >
                <font-awesome-icon v-b-tooltip.hover title="Edit Field" icon="edit" />
              </b-button>
              <b-button
                size="sm"
                type="button"
                variant="outline-danger"
                @click="onDeleteIdentifier(data)"
              >
                <font-awesome-icon v-b-tooltip.hover title="Delete Field" icon="trash" />
              </b-button>
            </div>
          </template>
        </b-table>
        <b-row>
          <b-col cols="2">
            <b-form-select v-model="perPage" id="perPageSelect" size="sm" :options="pageOptions"></b-form-select>
          </b-col>
          <b-col>
            <b-pagination
              class="float-right"
              v-model="currentPage"
              :total-rows="rows"
              :per-page="perPage"
              first-text="First"
              prev-text="Prev"
              next-text="Next"
              last-text="Last"
              aria-controls="my-table"
            ></b-pagination>
          </b-col>
        </b-row>
      </b-container>
    </b-overlay>
    <b-modal
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      scrollable
      size="lg"
      :title="editIdentifier ? 'Update Identifiers' : 'Create Identifiers'"
      v-model="showModal"
    >
      <div>
        <div v-if="!submiting && !responseSuccess">
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Title :
                  <span class="required-span">*</span>
                </label>
                <b-form-input
                  v-model="$v.form.titleInput.$model"
                  :state="$v.form.titleInput.$dirty ? !$v.form.titleInput.$error : null"
                  placeholder="Enter Title"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Identifier :
                  <span class="required-span">*</span>
                </label>
                <b-form-input
                  v-model="$v.form.identifiersInput.$model"
                  :state="$v.form.identifiersInput.$dirty ? !$v.form.identifiersInput.$error : null"
                  placeholder="Enter Title"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group label="Description :">
                <b-form-textarea
                  v-model="form.descriptionInput"
                  rows="2"
                  max-rows="2"
                  placeholder="Enter Description"
                ></b-form-textarea>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>Score:</label>
                <b-form-input
                  v-model="$v.form.scoreInput.$model"
                  :state="$v.form.scoreInput.$dirty ? !$v.form.scoreInput.$error : null"
                  placeholder="Enter Score"
                  type="number"
                  min="0"
                  max="100"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Entity Type :
                  <span class="required-span">*</span>
                </label>
                <b-form-radio-group
                  v-model="$v.form.entityType.$model"
                  :state="$v.form.entityType.$dirty ? !$v.form.entityType.$error : null"
                  :options="entityTypeOptions"
                ></b-form-radio-group>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Input Type :
                  <span class="required-span">*</span>
                </label>
                <b-form-radio-group
                  v-model="$v.form.inputType.$model"
                  :state="$v.form.inputType.$dirty ? !$v.form.inputType.$error : null"
                  :options="inputTypeOptions"
                ></b-form-radio-group>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Data Type :
                  <span class="required-span">*</span>
                </label>
                <b-form-radio-group
                  v-model="$v.form.dataType.$model"
                  :state="$v.form.dataType.$dirty ? !$v.form.dataType.$error : null"
                  :options="dataTypeOptions"
                ></b-form-radio-group>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
        <div v-else-if="submiting && !responseSuccess">
          <p>{{editIdentifier ? 'Your Identifiers is being Updating......' : 'Your Identifiers is being Creating......'}}</p>
          <b-spinner type="grow" label="Loading..."></b-spinner>
        </div>
        <div v-else>
          <p class="my-2 text-center">
            {{ editIdentifier ? 'Your Identifiers has been Updated successfully.' : 'Your Identifiers has been Created Successfully.' }}
            <br />
          </p>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button v-if="!submiting" variant="outline-danger" @click="resetModal">Close</b-button>
        <b-button
          v-if="!submiting && !responseSuccess"
          variant="outline-success"
          @click="checkValidation()"
        >{{editIdentifier ? 'Update' : 'Save' }}</b-button>
      </template>
    </b-modal>
    <b-overlay :show="showDeleteIdentifiers" no-wrap>
      <template v-slot:overlay>
        <div v-if="processing" class="text-center p-4 bg-primary text-light rounded">
          <font-awesome-icon size="lg" icon="cloud-upload-alt" />
          <div class="mb-3">Deleting...</div>
          <b-progress
            min="1"
            max="20"
            :value="counter"
            variant="success"
            height="3px"
            class="mx-n4 rounded-0"
          ></b-progress>
        </div>
        <div
          v-else
          ref="dialog"
          tabindex="-1"
          role="dialog"
          aria-modal="false"
          aria-labelledby="form-confirm-label"
          class="text-center p-3"
        >
          <p>
            <strong id="form-confirm-label">Are you sure?</strong>
          </p>
          <div class="d-flex">
            <b-button
              variant="outline-success"
              class="mr-3"
              @click="showDeleteIdentifiers = false"
            >Cancel</b-button>
            <b-button variant="outline-danger" @click="deleteIdentifiersCall">Yes</b-button>
          </div>
        </div>
      </template>
    </b-overlay>
  </div>
</template>
<script>
import { HTTP } from "../../utils/HTTP";
import { validationMixin } from "vuelidate";
import { required, between } from "vuelidate/lib/validators";
import uid from "uid";
export default {
  name: "ClientProfileIdentifiers",
  mixins: [validationMixin],
  data() {
    return {
      form: {
        titleInput: null,
        identifiersInput: null,
        descriptionInput: null,
        dataType: "String",
        inputType: null,
        entityType: null,
        scoreInput: null,
      },
      dataTypeOptions: [
        { text: "Array", value: "Array" },
        { text: "String", value: "String" },
        // { text: "Number", value: "Number" },
        // { text: "Object", value: "Object" },
        // { text: "Boolean", value: "Boolean" },
      ],
      inputTypeOptions: [
        { text: "Text", value: "Text" },
        { text: "Number", value: "Number" },
        { text: "Text Area", value: "Text Area" },
        // { text: "Select", value: "Select" },
        // { text: "Checkbox", value: "Checkbox" },
        // { text: "Radio", value: "Radio" },
        { text: "Date", value: "Date" },
      ],
      entityTypeOptions: [
        { text: "Organization", value: "O" },
        { text: "Individuals", value: "P" },
      ],
      showDeleteIdentifiers: false,
      submiting: false,
      responseSuccess: false,
      isLoading: false,
      showModal: false,
      tableFields: [
        { key: "title", label: "Title", sortable: true },
        { key: "title", label: "Title", sortable: true },
        { key: "identifier", label: "Identifier", sortable: true },
        { key: "description", label: "Description", sortable: true },
        { key: "score", label: "Score", sortable: true },
        { key: "data_type", label: "Data Type", sortable: true },
        { key: "entity_type", label: "Entity Type", sortable: true },
        { key: "actions", label: "Actions", sortable: false },
      ],
      tableItems: [],
      filter: null,
      perPage: 10,
      totalRows: 0,
      currentPage: 1,
      sortBy: "id",
      sortDesc: true,
      pageOptions: [5, 10, 25, 50, 75, 100],
      processing: false,
      counter: 1,
      interval: null,
      tableItemsIndex: null,
      editIdentifier: null,
      clientData: null,
    };
  },
  validations: {
    form: {
      titleInput: { required },
      identifiersInput: { required },
      dataType: { required },
      inputType: { required },
      entityType: { required },
      scoreInput: { between: between(0, 100) },
    },
  },
  mounted() {
    this.getIdentifiersTableData();
    this.getClientCustomPriFields();
  },
  computed: {
    rows() {
      return this.tableItems.length;
    },
  },
  beforeDestroy() {
    this.clearInterval();
  },
  methods: {
    clearInterval() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    async getClientCustomPriFields() {
      let token = "Bearer " + localStorage.getItem("accessToken");
      this.isLoading = true;
      try {
        let result = await HTTP(
          "get",
          "v1/pri/getclientcustomprifields/" + this.$route.params.id,
          null,
          {
            Authorization: token,
          },
          null,
          true
        );
        this.clientData = result.data.client;
        this.isLoading = false;
      } catch {
        this.isLoading = false;
        this.showErrorToast = true;
      }
    },
    async getIdentifiersTableData() {
      this.tableItems = [];
      let formData = {
        company_id: this.$route.params.id,
      };

      let token = "Bearer " + localStorage.getItem("accessToken");
      this.isLoading = true;
      let result = HTTP("post", "v1/get-identifiers-list", formData, {
        Authorization: token,
      });
      return result
        .then((response) => {
          response.data.map((identifiersData) => {
            if (identifiersData.identifiers) {
              let identifiersValue = JSON.parse(identifiersData.identifiers);
              this.tableItems = identifiersValue;
            }
            this.isLoading = false;
          });
        })
        .catch((error) => {
          this.isLoading = false;
          return [error];
        });
    },
    resetForm() {
      this.form = {
        titleInput: null,
        identifiersInput: null,
        descriptionInput: null,
        dataType: null,
        inputType: null,
      };
      this.$v.$reset();
    },
    resetModal() {
      this.resetForm();
      this.editIdentifier = false;
      this.tableItemsIndex = null;
      this.submiting = false;
      this.responseSuccess = false;
      this.isLoading = false;
      this.showModal = false;
    },
    checkValidation() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      } else if (this.editIdentifier) {
        this.updateIdentifierCall();
      } else {
        this.saveIdentifiers().then(this.getIdentifiersTableData);
      }
    },
    async saveIdentifiers() {
      let formData = {
        company_id: this.$route.params.id,
        identifiers: {
          id: uid(),
          title: this.form.titleInput,
          identifier: this.form.identifiersInput,
          description: this.form.descriptionInput,
          data_type: this.form.dataType,
          input_type: this.form.inputType,
          entity_type: this.form.entityType,
          score: this.form.scoreInput,
          value: this.form.dataType == "Array" ? [] : null,
        },
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP("post", "v1/add-identifiers-vendor-edu", formData, {
          Authorization: token,
        });
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (err) {
        // console.log("------------erroror", err);
      }
    },
    onEditIdentifier(data) {
      this.tableItemsIndex = data.index;
      this.editIdentifier = true;
      this.form = {
        titleInput: data.item.title,
        identifiersInput: data.item.identifier,
        descriptionInput: data.item.description,
        dataType: data.item.data_type,
        inputType: data.item.input_type,
        entityType: data.item.entity_type,
        scoreInput: data.item.score,
      };
      this.showModal = true;
    },
    updateIdentifierCall() {
      this.tableItems[this.tableItemsIndex].title = this.form.titleInput;
      this.tableItems[
        this.tableItemsIndex
      ].identifier = this.form.identifiersInput;
      this.tableItems[
        this.tableItemsIndex
      ].description = this.form.descriptionInput;
      this.tableItems[this.tableItemsIndex].data_type = this.form.dataType;
      this.tableItems[this.tableItemsIndex].input_type = this.form.inputType;
      this.tableItems[this.tableItemsIndex].entity_type = this.form.entityType;
      this.tableItems[this.tableItemsIndex].score = this.form.scoreInput;
      this.tableItems[this.tableItemsIndex].score = null;
      // console.log(this.tableItems[this.tableItemsIndex]);
      this.updateIdentifier().then(this.getIdentifiersTableData);
    },
    async updateIdentifier() {
      let formData = {
        company_id: this.$route.params.id,
        identifiersdata: this.tableItems,
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        let result = await HTTP("post", "v1/update-identifier", formData, {
          Authorization: token,
        });
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (err) {
        // console.log("------------erroror", err);
      }
    },
    onDeleteIdentifier(data) {
      this.showDeleteIdentifiers = true;
      this.tableItemsIndex = this.tableItems.indexOf(data.item);
    },
    deleteIdentifiersCall() {
      this.counter = 1;
      this.processing = true;
      this.tableItems.splice(this.tableItemsIndex, 1);
      this.deleteIdentifiers();
      this.clearInterval();
      this.interval = setInterval(() => {
        if (this.counter < 20) {
          this.counter = this.counter + 1;
        } else {
          this.clearInterval();
          this.$nextTick(() => {
            this.showDeleteIdentifiers = this.processing = false;
            this.getIdentifiersTableData();
          });
        }
      }, 350);
      this.tableItemsIndex = null;
    },
    async deleteIdentifiers() {
      let formData = {
        company_id: this.$route.params.id,
        identifiersdata: this.tableItems,
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        let result = await HTTP("post", "v1/delete-identifier", formData, {
          Authorization: token,
        });
      } catch (err) {
        // console.log("------------erroror", err);
      }
    },
  },
};
</script>